import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface ImportTransaktion {
  id: string;
  firma: string;
  buchung_datum: string;
  wert_datum: string | null;
  betrag: number;
  waehrung: string;
  beschreibung: string | null;
  rohtext: string | null;
  status: 'sicher' | 'unklar' | 'geklaert' | 'ignoriert';
  kategorie: string | null;
  klaerung_grund: string | null;
  klaerung_antwort: string | null;
  klaerung_kategorie: string | null;
  klaerung_kommentar: string | null;
  klaerung_datum: string | null;
  ist_einnahme: boolean;
  ist_geschaeftlich: boolean;
  zugeordnet_konto_id: string | null;
  created_at: string;
}

export type TransaktionKategorie =
  | 'versicherung'
  | 'anwalt_regulierung'
  | 'gutachten_zahlung'
  | 'interne_verrechnung'
  | 'steuerberater'
  | 'sozialversicherung'
  | 'software'
  | 'steuern_abgaben'
  | 'bankgebuehren'
  | 'leasing_kredit'
  | 'dat_lizenz'
  | 'post_gutschrift'
  | 'zulieferer'
  | 'provision'
  | 'miete'
  | 'gehalt'
  | 'werbung_marketing'
  | 'buero_material'
  | 'fahrzeug'
  | 'tankstelle'
  | 'essen_bewirtung'
  | 'privat'
  | 'sonstiges';

export const KATEGORIE_LABELS: Record<TransaktionKategorie, string> = {
  versicherung: 'Versicherung',
  anwalt_regulierung: 'Anwalt / Regulierung',
  gutachten_zahlung: 'Gutachten-Zahlung',
  interne_verrechnung: 'Interne Verrechnung',
  steuerberater: 'Steuerberater',
  sozialversicherung: 'Sozialversicherung',
  software: 'Software / IT',
  steuern_abgaben: 'Steuern & Abgaben',
  bankgebuehren: 'Bankgebühren',
  leasing_kredit: 'Leasing / Kredit',
  dat_lizenz: 'DAT-Lizenz',
  post_gutschrift: 'Post / Porto',
  zulieferer: 'Zulieferer / Material',
  provision: 'Provision',
  miete: 'Miete / Pacht',
  gehalt: 'Gehalt / Lohn',
  werbung_marketing: 'Werbung / Marketing',
  buero_material: 'Büro / Material',
  fahrzeug: 'Fahrzeug / KFZ',
  tankstelle: 'Tankstelle / Kraftstoff',
  essen_bewirtung: 'Essen / Bewirtung',
  privat: 'Privat (nicht geschäftlich)',
  sonstiges: 'Sonstiges',
};

export interface TransaktionStats {
  gesamt: number;
  sicher: number;
  unklar: number;
  geklaert: number;
  ignoriert: number;
  summe_einnahmen: number;
  summe_ausgaben: number;
}

export function useImportTransaktionen() {
  const [transaktionen, setTransaktionen] = useState<ImportTransaktion[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<TransaktionStats>({
    gesamt: 0, sicher: 0, unklar: 0, geklaert: 0, ignoriert: 0,
    summe_einnahmen: 0, summe_ausgaben: 0,
  });

  const loadTransaktionen = useCallback(async (
    filter?: {
      status?: string;
      firma?: string;
      kategorie?: string;
      suche?: string;
      nur_einnahmen?: boolean;
      nur_ausgaben?: boolean;
    },
    page = 0,
    pageSize = 50,
  ) => {
    setLoading(true);
    try {
      let query = supabase
        .from('import_transaktionen')
        .select('*', { count: 'exact' })
        .order('buchung_datum', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (filter?.status) {
        query = query.eq('status', filter.status);
      }
      if (filter?.firma) {
        query = query.eq('firma', filter.firma);
      }
      if (filter?.kategorie) {
        query = query.eq('kategorie', filter.kategorie);
      }
      if (filter?.suche) {
        query = query.ilike('beschreibung', `%${filter.suche}%`);
      }
      if (filter?.nur_einnahmen) {
        query = query.gt('betrag', 0);
      }
      if (filter?.nur_ausgaben) {
        query = query.lt('betrag', 0);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      setTransaktionen(data || []);
      return { data: data || [], count: count || 0 };
    } catch (err) {
      console.error('Fehler beim Laden der Transaktionen:', err);
      return { data: [], count: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      // Separate Count-Queries um Supabase 1000-Zeilen-Limit zu umgehen
      const [gesamt, sicher, unklar, geklaert, ignoriert, einnahmen, ausgaben] = await Promise.all([
        supabase.from('import_transaktionen').select('id', { count: 'exact', head: true }),
        supabase.from('import_transaktionen').select('id', { count: 'exact', head: true }).eq('status', 'sicher'),
        supabase.from('import_transaktionen').select('id', { count: 'exact', head: true }).eq('status', 'unklar'),
        supabase.from('import_transaktionen').select('id', { count: 'exact', head: true }).eq('status', 'geklaert'),
        supabase.from('import_transaktionen').select('id', { count: 'exact', head: true }).eq('status', 'ignoriert'),
        supabase.from('import_transaktionen').select('betrag').gt('betrag', 0),
        supabase.from('import_transaktionen').select('betrag').lt('betrag', 0),
      ]);

      // Einnahmen/Ausgaben summieren (können > 1000 sein, daher in Batches)
      let sumEinnahmen = 0;
      let sumAusgaben = 0;
      if (einnahmen.data) sumEinnahmen = einnahmen.data.reduce((s: number, t: { betrag: number }) => s + t.betrag, 0);
      if (ausgaben.data) sumAusgaben = ausgaben.data.reduce((s: number, t: { betrag: number }) => s + Math.abs(t.betrag), 0);

      const s: TransaktionStats = {
        gesamt: gesamt.count || 0,
        sicher: sicher.count || 0,
        unklar: unklar.count || 0,
        geklaert: geklaert.count || 0,
        ignoriert: ignoriert.count || 0,
        summe_einnahmen: sumEinnahmen,
        summe_ausgaben: sumAusgaben,
      };
      setStats(s);
    } catch (err) {
      console.error('Fehler beim Laden der Stats:', err);
    }
  }, []);

  const klaerenTransaktion = useCallback(async (
    id: string,
    klaerung: {
      kategorie: TransaktionKategorie;
      kommentar?: string;
      ist_geschaeftlich: boolean;
      antwort?: string;
    }
  ) => {
    try {
      const { error } = await supabase
        .from('import_transaktionen')
        .update({
          status: 'geklaert',
          klaerung_kategorie: klaerung.kategorie,
          klaerung_kommentar: klaerung.kommentar || null,
          klaerung_antwort: klaerung.antwort || null,
          ist_geschaeftlich: klaerung.ist_geschaeftlich,
          klaerung_datum: new Date().toISOString(),
          kategorie: klaerung.kategorie,
        })
        .eq('id', id);

      if (error) throw error;

      // Lokalen State aktualisieren
      setTransaktionen(prev =>
        prev.map(t => t.id === id ? {
          ...t,
          status: 'geklaert' as const,
          klaerung_kategorie: klaerung.kategorie,
          klaerung_kommentar: klaerung.kommentar || null,
          ist_geschaeftlich: klaerung.ist_geschaeftlich,
          kategorie: klaerung.kategorie,
        } : t)
      );

      return true;
    } catch (err) {
      console.error('Fehler beim Klären:', err);
      return false;
    }
  }, []);

  const ignorierenTransaktion = useCallback(async (id: string, kommentar?: string) => {
    try {
      const { error } = await supabase
        .from('import_transaktionen')
        .update({
          status: 'ignoriert',
          klaerung_kommentar: kommentar || 'Ignoriert',
          klaerung_datum: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      setTransaktionen(prev =>
        prev.map(t => t.id === id ? { ...t, status: 'ignoriert' as const } : t)
      );
      return true;
    } catch (err) {
      console.error('Fehler beim Ignorieren:', err);
      return false;
    }
  }, []);

  const bulkKlaeren = useCallback(async (
    ids: string[],
    klaerung: {
      kategorie: TransaktionKategorie;
      kommentar?: string;
      ist_geschaeftlich: boolean;
    }
  ) => {
    try {
      const { error } = await supabase
        .from('import_transaktionen')
        .update({
          status: 'geklaert',
          klaerung_kategorie: klaerung.kategorie,
          klaerung_kommentar: klaerung.kommentar || null,
          ist_geschaeftlich: klaerung.ist_geschaeftlich,
          klaerung_datum: new Date().toISOString(),
          kategorie: klaerung.kategorie,
        })
        .in('id', ids);

      if (error) throw error;

      setTransaktionen(prev =>
        prev.map(t => ids.includes(t.id) ? {
          ...t,
          status: 'geklaert' as const,
          klaerung_kategorie: klaerung.kategorie,
          ist_geschaeftlich: klaerung.ist_geschaeftlich,
          kategorie: klaerung.kategorie,
        } : t)
      );

      return true;
    } catch (err) {
      console.error('Fehler beim Bulk-Klären:', err);
      return false;
    }
  }, []);

  // Ähnliche Transaktionen finden (gleicher Empfänger/Sender)
  const findeSimilar = useCallback(async (beschreibung: string) => {
    try {
      // Extrahiere den Hauptnamen (erste Zeile / erstes Wort vor Details)
      const name = beschreibung.split(/\s+/).slice(0, 3).join(' ');
      
      const { data } = await supabase
        .from('import_transaktionen')
        .select('*')
        .ilike('beschreibung', `%${name}%`)
        .order('buchung_datum', { ascending: false })
        .limit(20);

      return data || [];
    } catch (err) {
      console.error('Fehler beim Suchen ähnlicher:', err);
      return [];
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    transaktionen,
    loading,
    stats,
    loadTransaktionen,
    loadStats,
    klaerenTransaktion,
    ignorierenTransaktion,
    bulkKlaeren,
    findeSimilar,
  };
}
